import { Modal, Input, Badge, Button, Tabs, Dropdown } from "antd";
import {
    SearchOutlined,
    FilterOutlined,
    CloseOutlined,
    CheckCircleFilled
} from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ConnectCodeView from "./ConnectCodeView";
import PendingView from "./PendingView";
import HiddenView from "./HiddenView";
import ConnectFacebookView from "./ConnectFacebookView";
import ConnectInstagramView from "./ConnectInstagramView";
import ConnectTiktokView from "./ConnectTiktokView";
import ConnectZaloView from "./ConnectZaloView";

type PageType = {
    id: string;
    name: string;
    username: string;
};

const pages: PageType[] = [
    {
        id: "1",
        name: "Buso - Thiết kế Website",
        username: "busothietkeweb",
    },
    {
        id: "2",
        name: "Marketing Online",
        username: "marketingonline",
    },
];

type Props = {
    open: boolean;
    onClose: () => void;
};

type SidebarItem = {
    key: string;
    label: string;
    icon?: string;
    count?: number;
};

const getSidebarItems = (t: any): SidebarItem[] => [
    {
        key: "pending",
        label: t("chats.pending_activate"),
        count: 2,
        icon: "https://pancake.vn/static/images/platform-connection/wait-activate.png",
    },
    {
        key: "facebook",
        label: "Facebook",
        icon: "https://pancake.vn/static/images/platform-connection/facebook.png",
    },
    // {
    //     key: "instagram",
    //     label: "Instagram",
    //     icon: "https://pancake.vn/static/images/platform-connection/instagram.png",
    // },
    {
        key: "tiktok",
        label: "TikTok",
        icon: "https://pancake.vn/static/images/platform-connection/tiktok.png",
    },
    {
        key: "zalo",
        label: "Zalo",
        icon: "https://pancake.vn/static/images/platform-connection/zalo.png",
    },
];

export default function ConnectModal({ open, onClose }: Props) {
    const { t } = useTranslation();
    const sidebarItems = getSidebarItems(t);
    const [filter, setFilter] = useState(t("chats.tab_all"));
    const [activeSidebar, setActiveSidebar] = useState("pending");
    const [activeTab, setActiveTab] = useState("pending");
    const [selectedPlatform, setSelectedPlatform] = useState<{
        key: string;
        icon: React.ReactNode;
    } | null>({
        key: "all",
        icon: <FilterOutlined />,
    });

    const platformItems = [
        {
            key: "all",
            label: t("chats.tab_all"),
            icon: <FilterOutlined className="text-gray-600" />,
        },
        {
            key: "facebook",
            label: "Facebook",
            icon: (
                <img
                    src="https://pancake.vn/static/images/platform-connection/facebook.png"
                    className="w-4 h-4"
                    alt="Facebook"
                />
            ),
        },
        {
            key: "instagram",
            label: "Instagram",
            icon: (
                <img
                    src="https://pancake.vn/static/images/platform-connection/instagram.png"
                    className="w-4 h-4"
                    alt="Instagram"
                />
            ),
        },
        {
            key: "tiktok",
            label: "TikTok",
            icon: (
                <img
                    src="https://pancake.vn/static/images/platform-connection/tiktok.png"
                    className="w-4 h-4"
                    alt="TikTok"
                />
            ),
        },
        {
            key: "zalo",
            label: "Zalo",
            icon: (
                <img
                    src="https://pancake.vn/static/images/platform-connection/zalo.png"
                    className="w-4 h-4"
                    alt="Zalo"
                />
            ),
        },
    ];

    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id]
        );
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={window.innerWidth < 768 ? "100%" : 950}
            closable={false}
        >
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{t('chats.add_connection')}</h2>
                <CloseOutlined
                    onClick={onClose}
                    className="cursor-pointer text-gray-500 hover:text-black"
                />
            </div>

            <div
                className="
                    grid 
                    grid-cols-1 
                    md:grid-cols-[200px_1fr] 
                    h-auto 
                    md:h-[680px]
                ">

                {/* SIDEBAR */}
                <div className="
                    border-b
                    md:border-b-0
                    md:border-r
                    pr-0
                    md:pr-4
                    pb-4
                    md:pb-0
                    overflow-x-auto
                    md:overflow-y-auto
                    mb-4
                    md:mb-0
                ">
                    <div className="
                        flex
                        md:flex-col
                        gap-2
                        md:gap-1
                        min-w-max
                    ">
                        {sidebarItems.map((item) => {
                            const isActive = activeSidebar === item.key;

                            return (
                                <div
                                    key={item.key}
                                    onClick={() => setActiveSidebar(item.key)}
                                    className={`
                                        flex items-center justify-between
                                        p-2 rounded-lg cursor-pointer text-sm
                                        transition-all duration-200
                                        ${isActive
                                            ? "bg-gray-100 font-medium"
                                            : "hover:bg-gray-50 text-gray-600"
                                        }
                                    `}
                                >
                                    {/* LEFT SIDE */}
                                    <div className="flex items-center gap-2">
                                        {item.icon && (
                                            <img
                                                src={item.icon}
                                                alt={item.label}
                                                className={`
                                                    w-[32px] h-[32px] object-contain rounded-lg
                                                    transition-all duration-200
                                                    ${isActive
                                                        ? "shadow-[0px_3px_8px_rgba(0,0,0,0.15)] bg-white"
                                                        : ""
                                                    }
                                                `}
                                            />
                                        )}
                                        {item.label}
                                    </div>

                                    {/* RIGHT BADGE */}
                                    {item.count !== undefined && (
                                        <div>
                                            <Badge count={item.count} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* CONTENT */}
                {activeSidebar === "pending" &&
                    <div className="
                        pt-4 
                        md:pt-0 
                        md:pl-6 
                        flex 
                        flex-col 
                        h-auto 
                        md:h-full
                    ">
                        {/* TITLE + SEARCH */}
                        <div className="
                            flex 
                            flex-col 
                            md:flex-row 
                            md:justify-between 
                            md:items-center 
                            gap-3 
                            mb-4
                        ">
                            <div className="text-base font-medium">
                                {t('chats.select_page_activate')}
                            </div>

                            <div className="flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder={t('chats.search_page')}
                                    className="w-full md:w-[220px]"
                                />

                                <Dropdown
                                    menu={{
                                        items: platformItems,
                                        onClick: (e) => {
                                            const item = platformItems.find(i => i.key === e.key);
                                            if (item) {
                                                setSelectedPlatform({
                                                    key: item.key,
                                                    icon: item.icon,
                                                });
                                            }
                                        },
                                    }}
                                >
                                    <Button className="w-10 h-10 p-0 flex items-center justify-center border border-gray-200 hover:border-[#3e91ab]">
                                        {selectedPlatform?.icon}
                                    </Button>
                                </Dropdown>
                            </div>
                        </div>

                        <div className="flex justify-between items-center mb-4">
                            {/* TABS */}
                            <Tabs
                                className="connect-tabs"
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={[
                                    {
                                        key: "pending",
                                        label: (
                                            <div className="flex items-center gap-2">
                                                {t('chats.pending_activate')}
                                                <Badge count={1} />
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "hidden",
                                        label: t('chats.hidden'),
                                    },
                                    {
                                        key: "connect",
                                        label: (
                                            <div className="flex items-center gap-1 text-[#357f96]">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256"><path d="M216.57,39.43A80,80,0,0,0,83.91,120.78L28.69,176A15.86,15.86,0,0,0,24,187.31V216a16,16,0,0,0,16,16H72a8,8,0,0,0,8-8V208H96a8,8,0,0,0,8-8V184h16a8,8,0,0,0,5.66-2.34l9.56-9.57A79.73,79.73,0,0,0,160,176h.1A80,80,0,0,0,216.57,39.43ZM224,98.1c-1.09,34.09-29.75,61.86-63.89,61.9H160a63.7,63.7,0,0,1-23.65-4.51,8,8,0,0,0-8.84,1.68L116.69,168H96a8,8,0,0,0-8,8v16H72a8,8,0,0,0-8,8v16H40V187.31l58.83-58.82a8,8,0,0,0,1.68-8.84A63.72,63.72,0,0,1,96,95.92c0-34.14,27.81-62.8,61.9-63.89A64,64,0,0,1,224,98.1ZM192,76a12,12,0,1,1-12-12A12,12,0,0,1,192,76Z"></path></svg>
                                                {t('chats.connect_code')}
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        {activeTab === "pending" && <PendingView />}
                        {activeTab === "hidden" && <HiddenView />}
                        {activeTab === "connect" && <ConnectCodeView />}
                    </div>
                }
                {activeSidebar === "facebook" && <ConnectFacebookView />}
                {activeSidebar === "instagram" && <ConnectInstagramView />}
                {activeSidebar === "tiktok" && <ConnectTiktokView />}
                {activeSidebar === "zalo" && <ConnectZaloView />}
            </div>
        </Modal>
    );
}